import { useState } from "react";
import { Container } from "@/components/Container";
import { adminQueries } from "@/api/api";
import { H4 } from "@/components/ui/h4";
import { TenantTabContent } from "@/components/admin/TenantTabContent";
import { useQueryClient } from "@tanstack/react-query";
import { UserTabContent } from "@/components/admin/UserTabContent";
import { Button } from "@/components/ui/button";
import { ProductTabContent } from "@/components/admin/ProductTabContent";
import { CreditGrantManagement } from "@/components/admin/CreditGrantManagement";
import { ProductAnalyticsPage } from "@/components/admin/product-analytics/ProductAnalyticsPage";
import { UserAnalytics } from "@/components/account/user-analytics/UserAnalytics";
import { creditQueries } from "@/api/credits";
import { AgentsStatusTable } from "@/components/admin/AgentsStatusTable";
import { useTabUrlState } from "@/hooks/use-tab-url-state";
import BettingDashboard from "@/components/betting/BettingDashboard";

const Tab = {
  user: "user",
  tenant: "tenant",
  product: "product",
  credits: "credits",
  userAnalytics: "user-analytics",
  productAnalytics: "product-analytics",
  agents: "agents",
  betting: "betting", // Added new tab for betting
} as const;

type Tab = (typeof Tab)[keyof typeof Tab];

const TABS = Object.values(Tab);

const Admin = () => {
  const queryClient = useQueryClient();
  const [tab, setTab] = useTabUrlState(TABS);
  const [selectedUsername, setSelectedUsername] = useState<string | null>(null);

  const handleTabChange = (newTab: Tab) => {
    setTab(newTab);

    switch (newTab) {
      case Tab.user:
        queryClient.invalidateQueries(adminQueries.users());
        break;
      case Tab.tenant:
        queryClient.invalidateQueries(adminQueries.tenants());
        break;
      case Tab.product:
        // @ts-expect-error nonexistent query
        queryClient.invalidateQueries(adminQueries.products?.());
        break;
      case Tab.credits:
        queryClient.invalidateQueries(creditQueries.promoCodes());
        break;
      case Tab.userAnalytics:
      case Tab.productAnalytics:
      case Tab.agents:
      case Tab.betting: // Handle new betting tab
        break;
      default:
        break;
    }

    setSelectedUsername(null);
  };

  const handleUserLinkClick = (username: string) => {
    setTab(Tab.user);
    setSelectedUsername(username);
  };

  return (
    <Container>
      <H4 className="text-primary text-center font-medium py-4">
        Admin Center
      </H4>

      {/* Management Buttons */}
      <div className="flex justify-center gap-4 my-8 flex-wrap">
        <Button
          variant={tab === Tab.user ? "default" : "outline"}
          onClick={() => handleTabChange(Tab.user)}
          className="min-w-[150px]"
        >
          User Manager
        </Button>

        <Button
          variant={tab === Tab.tenant ? "default" : "outline"}
          onClick={() => handleTabChange(Tab.tenant)}
          className="min-w-[150px]"
        >
          Tenant Manager
        </Button>

        <Button
          variant={tab === Tab.product ? "default" : "outline"}
          onClick={() => handleTabChange(Tab.product)}
          className="min-w-[150px]"
        >
          Product Manager
        </Button>

        <Button
          variant={tab === Tab.credits ? "default" : "outline"}
          onClick={() => handleTabChange(Tab.credits)}
          className="min-w-[150px]"
        >
          Credit Manager
        </Button>

        <Button
          variant={tab === Tab.userAnalytics ? "default" : "outline"}
          onClick={() => handleTabChange(Tab.userAnalytics)}
          className="min-w-[150px]"
        >
          User Analytics
        </Button>

        <Button
          variant={tab === Tab.productAnalytics ? "default" : "outline"}
          onClick={() => handleTabChange(Tab.productAnalytics)}
          className="min-w-[150px]"
        >
          Product Analytics
        </Button>

        <Button
          variant={tab === Tab.agents ? "default" : "outline"}
          onClick={() => handleTabChange(Tab.agents)}
          className="min-w-[150px]"
        >
          Agents Status
        </Button>

        {/* Betting Dashboard Button */}
        <Button
          variant={tab === Tab.betting ? "default" : "outline"}
          onClick={() => handleTabChange(Tab.betting)}
          className="min-w-[150px]"
        >
          Betting Dashboard
        </Button>
      </div>

      {/* Tab Content */}
      {tab === Tab.user && (
        <div className="flex flex-col gap-4 justify-center w-full max-w-[100%] mx-auto">
          <UserTabContent
            username={selectedUsername}
            onUsernameChange={(value) => setSelectedUsername(value)}
          />
        </div>
      )}

      {tab === Tab.tenant && (
        <div className="flex flex-col gap-4 justify-center max-w-[100%] mx-auto">
          <TenantTabContent onUserSelect={handleUserLinkClick} />
        </div>
      )}

      {tab === Tab.product && (
        <div className="flex flex-col gap-4 justify-center max-w-[100%] mx-auto">
          <ProductTabContent />
        </div>
      )}

      {tab === Tab.credits && (
        <div className="flex flex-col gap-4 justify-center max-w-[100%] mx-auto">
          <CreditGrantManagement />
        </div>
      )}

      {tab === Tab.userAnalytics && (
        <div className="flex flex-col gap-4 justify-center max-w-[100%] mx-auto">
          <UserAnalytics />
        </div>
      )}

      {tab === Tab.productAnalytics && (
        <div className="flex flex-col gap-4 justify-center max-w-[100%] mx-auto">
          <ProductAnalyticsPage />
        </div>
      )}

      {tab === Tab.agents && (
        <div>
          <H4>Agents Status Dashboard</H4>
          <div className="mt-4">
            <AgentsStatusTable />
          </div>
        </div>
      )}

      {/* Betting Dashboard Tab Content */}
      {tab === Tab.betting && (
        <div className="flex flex-col gap-4 justify-center max-w-[100%] mx-auto">
          <BettingDashboard />
        </div>
      )}
    </Container>
  );
};

export default Admin;
